<template>
    <div class="zs-textarea-container">
        <label v-if="label" :for="id" class="zs-textarea-label">
            {{ label }}
            <span v-if="required" class="required-mark">*</span>
        </label>
        <textarea :id="id" v-bind="$attrs" :value="modelValue" :placeholder="placeholder" :required="required"
            :disabled="disabled" :readonly="readonly"
            :class="['zs-textarea', { 'prevent-line-breaks': preventLineBreaks, 'invalid': hasError }]"
            @input="handleInput" @keydown="handleKeyDown" @focus="$emit('focus')" @blur="$emit('blur')"
            ref="textareaRef"></textarea>
        <small class="help-error" v-show="hasError">{{ validationResult.description || errorMessage }}</small>
        <p v-if="!hasError && helpText" class="help-text">{{ helpText }}</p>
    </div>
</template>
<script setup>
import { ref, computed, onMounted, inject, watch } from 'vue';

const props = defineProps({
    modelValue: {
        type: [String, Number],
        default: ''
    },
    label: {
        type: String,
        default: ''
    },
    placeholder: {
        type: String,
        default: ''
    },
    required: {
        type: Boolean,
        default: false
    },
    disabled: {
        type: Boolean,
        default: false
    },
    readonly: {
        type: Boolean,
        default: false
    },
    helpText: {
        type: String,
        default: ''
    },
    errorMessage: {
        type: String,
        default: ''
    },
    preventLineBreaks: {
        type: Boolean,
        default: false
    },
    // Added validation-related props
    field: {
        type: String,
        default: ''
    },
    rules: {
        type: String,
        default: ''
    },
    toValidate: {
        type: Boolean,
        default: true
    },
    validateOnChange: {
        type: Boolean,
        default: true
    }
});

const emit = defineEmits(['update:modelValue', 'validation', 'focus', 'blur']);
const id = ref(`textarea-${uuidv4()}`);
const validator = inject('validator', null);
const validationResult = ref({});
const textareaRef = ref(null);
const internalValue = ref(props.modelValue);

// Compute if there's an error to show
const hasError = computed(() => {
    return (validationResult.value && validationResult.value.description) || props.errorMessage;
});

// UUID generator
function uuidv4() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        var r = Math.random() * 16 | 0,
            v = c == 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
}

const handleInput = (event) => {
    let value = event.target.value;

    // If preventLineBreaks is true, remove any line breaks
    if (props.preventLineBreaks) {
        value = value.replace(/[\r\n]+/g, ' ');
        // Update the textarea value to reflect the change
        event.target.value = value;
    }

    // Validate the input value if validateOnChange is true
    if (props.validateOnChange) {
        validateData(value);
    }

    internalValue.value = value;
    emit('update:modelValue', value);
};

const handleKeyDown = (event) => {
    // Prevent Enter key from creating new lines if preventLineBreaks is true
    if (props.preventLineBreaks && event.key === 'Enter') {
        event.preventDefault();
    }
};

// Validation functions
const validateData = (value) => {
    if (validator && props.rules !== '') {
        validationResult.value = validator.validate({
            id: id.value,
            value: value,
            rules: props.rules,
            field: props.field || props.label
        });

        emit('validation', validationResult.value);
    }
};

const reset = () => {
    validationResult.value = {};
};

const validate = () => {
    validateData(internalValue.value);
};

const subscribeToValidator = () => {
    if (validator) {
        validator.subscribe({ validate, reset });
    }
};

// Watch for changes to modelValue prop
watch(() => props.modelValue, (newValue) => {
    internalValue.value = newValue;
    if (props.validateOnChange) validateData(newValue);
});

// Watch for changes to rules prop
watch(() => props.rules, (newVal, oldVal) => {
    if (newVal !== oldVal) {
        validateData(internalValue.value);
    }
});

onMounted(() => {
    if (props.toValidate) subscribeToValidator();
    if (props.validateOnChange) validateData(props.modelValue);
});

// Expose methods to parent component
defineExpose({
    validate,
    reset,
    getRef: () => textareaRef.value
});
</script>
<style scoped>
.zs-textarea-container {
    /* TextArea Container */
    --zs-textarea-container-margin-bottom: 1rem;
    --zs-textarea-container-width: 100%;
    --zs-textarea-container-position: relative;

    position: relative;
    width: 100%;

    /* TextArea Label */
    --zs-textarea-label-font-size: 12px;
    --zs-textarea-label-font-family: "Poppins";
    --zs-textarea-label-color: var(--color-menu);
    --zs-textarea-label-padding-bottom: 5px;
    --zs-textarea-label-display: block;

    /* TextArea Field */
    --zs-textarea-width: 100%;
    --zs-textarea-padding: 0.75rem;
    --zs-textarea-font-size: 12px;
    --zs-textarea-border: 1px solid #a0c1de;
    --zs-textarea-border-radius: 5px;
    --zs-textarea-resize: vertical;
    --zs-textarea-transition: border-color 0.2s ease;

    /* TextArea Focus */
    --zs-textarea-focus-outline: none;
    --zs-textarea-focus-border-color: var(--color-textarea-focus-border);
    --zs-textarea-focus-box-shadow: 0 0 0 2px var(--color-textarea-focus-shadow);

    /* TextArea Invalid */
    --zs-textarea-invalid-border-color: var(--color-textarea-invalid-border);

    /* Required Mark */
    --zs-textarea-required-mark-color: var(--color-textarea-required-mark);

    /* Help Error */
    --zs-textarea-help-error-color: var(--color-textarea-help-error);
    --zs-textarea-help-error-font-size: 12px;
    --zs-textarea-help-error-position: absolute;
    --zs-textarea-help-error-height: 20px;
    --zs-textarea-help-error-left: 0px;
    --zs-textarea-help-error-bottom: -20px;

    /* Help Text */
    --zs-textarea-help-text-color: var(--color-textarea-help-text);
    --zs-textarea-help-text-font-size: 0.875rem;
    --zs-textarea-help-text-margin-top: 0.25rem;

    /* TextArea Disabled */
    --zs-textarea-disabled-bg-color: var(--color-textarea-disabled-bg);
    --zs-textarea-disabled-cursor: not-allowed;

    /* TextArea Readonly */
    --zs-textarea-readonly-bg-color: inherit;
    --zs-textarea-readonly-cursor: default;


}

.zs-textarea-label {
    font-size: var(--zs-textarea-label-font-size);
    font-family: var(--zs-textarea-label-font-family);
    color: var(--zs-textarea-label-color);
    padding-bottom: var(--zs-textarea-label-padding-bottom);
    display: var(--zs-textarea-label-display);
}

.zs-textarea {
    width: var(--zs-textarea-width);
    padding: var(--zs-textarea-padding);
    font-family: inherit;
    font-size: var(--zs-textarea-font-size);
    border: var(--zs-textarea-border);
    border-radius: var(--zs-textarea-border-radius);
    resize: var(--zs-textarea-resize);
    transition: var(--zs-textarea-transition);
}

.zs-textarea:focus {
    outline: var(--zs-textarea-focus-outline);
    border-color: var(--zs-textarea-focus-border-color);
    box-shadow: var(--zs-textarea-focus-box-shadow);
}

.zs-textarea.invalid {
    border-color: var(--zs-textarea-invalid-border-color);
}

.required-mark {
    color: var(--zs-textarea-required-mark-color);
}

.help-error {
    color: var(--zs-textarea-help-error-color);
    font-size: var(--zs-textarea-help-error-font-size);
    position: var(--zs-textarea-help-error-position);
    height: var(--zs-textarea-help-error-height);
    left: var(--zs-textarea-help-error-left);
    bottom: var(--zs-textarea-help-error-bottom);
    text-align: start;
    color: red;
}

.help-text {
    color: var(--zs-textarea-help-text-color);
    font-size: var(--zs-textarea-help-text-font-size);
    margin-top: var(--zs-textarea-help-text-margin-top);
}

.zs-textarea:disabled {
    border-color: rgba(211, 211, 211, 0.802);
    background-color: var(--zs-textarea-disabled-bg-color);
    cursor: var(--zs-textarea-disabled-cursor);
}

.zs-textarea[readonly] {
    background-color: var(--zs-textarea-readonly-bg-color);
    cursor: var(--zs-textarea-readonly-cursor);
}
</style>
